import {
  CError,
} from '../consoleMixin'
import {
  getAxiosClient,
} from './axios-client'
import {
  buildQueryFilter,
  buildSortParams,
} from './helpers'

/**
 * @typedef {import('./js-doc').V2CreateInvoiceRequest} V2CreateInvoiceRequest
 * @typedef {import('./js-doc').V2UpdateInvoiceRequest} V2UpdateInvoiceRequest
 * @typedef {import('./js-doc').V2PurchaseInvoiceRequest} V2PurchaseInvoiceRequest
 * @typedef {import('./js-doc').V2RejectInvoiceRequest} V2RejectInvoiceRequest
 * @typedef {import('./js-doc').V2VerifyInvoiceRequest} V2VerifyInvoiceRequest
 * @typedef {import('./js-doc').V2DeleteInvoiceRequest} V2DeleteInvoiceRequest
 * @typedef {import('./js-doc').V2RevertInvoiceRequest} V2RevertInvoiceRequest
 * @typedef {import('./js-doc').V2RegenerateInvoiceDocumentRequest} V2RegenerateInvoiceDocumentRequest
 * @typedef {import('./js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('./js-doc').V2PossibleDuplicatesResponse} V2PossibleDuplicatesResponse
 */

class Invoice {
  constructor () {
    this.baseURL = 'invoices'
  }

  /**
   * @param {string} invoiceId - ID of the invoice
   * @param {any} params - V2 assign tag request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async addTag (invoiceId, params) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).patch(`${invoiceId}/activity`, params)
      return result?.data
    }
    catch (error) {
      CError('Could not add tag to invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {V2CreateInvoiceRequest} v2Request - V2 invoice create request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async create (v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post('', v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not create invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2DeleteInvoiceRequest} v2Request - V2 delete invoice request body
   * @returns {Promise<void>}
   */
  async delete (id, v2Request) {
    try {
      await (await getAxiosClient(this.baseURL)).delete(id, { data: v2Request })
    }
    catch (error) {
      CError(`Could not delete invoice ${id} from V2 API`, error)
      throw error
    }
  }

  async deleteTag (invoiceId, activityId, params) {
    try {
      return await (await getAxiosClient(this.baseURL)).delete(`${invoiceId}/activity/${activityId}`, { data: params })
    }
    catch (error) {
      CError('Could not delete tag from invoice in V2 API', error)
      throw error
    }
  }

  // eslint-disable-next-line no-unused-vars
  async get (id) {
    try {
      return await (await getAxiosClient(this.baseURL)).get(id)
    }
    catch (error) {
      CError(`Could not retrieve invoice ${id} from V2 API`, error)
      throw error
    }
  }

  /**
   * @param {V2CreateInvoiceRequest} v2Request - V2 invoice create request body
   * @returns {Promise<V2PossibleDuplicatesResponse>} V2 response body
   */
  async getPossibleDuplicates (v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post('possible-duplicate-check', v2Request)
      return result?.data
    }
    catch (err) {
      CError(`Could not retrieve potential duplicate invoices from V2 API`, err)
      throw err
    }
  }

  // eslint-disable-next-line no-unused-vars
  async list (filterParams, sortParams, paginationParams) {
    const filter = buildQueryFilter(filterParams)
    const sort = buildSortParams(sortParams)
    try {
      return await (await getAxiosClient(this.baseURL)).get('', {
        params: {
          filter: filter,
          limit: paginationParams.limit,
          page: paginationParams.page,
          sort: sort,
        },
      })
    }
    catch (error) {
      CError(`Could not list invoices from V2 API`, error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2PurchaseInvoiceRequest} v2Request - V2 purchase invoice request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async purchase (id, v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post(`${id}/purchase`, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not purchase invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2RejectInvoiceRequest} v2Request - V2 reject invoice request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async reject (id, v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post(`${id}/reject`, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not reject invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2RegenerateInvoiceDocumentRequest} v2Request - V2 regenerate invoice document request body
   * @returns {Promise<void>}
   */
  async regenerateDocs (id, v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post(`${id}/regenerate-docs`, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not regenerate documents for invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2RevertInvoiceRequest} v2Request - V2 reject invoice request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async revert (id, v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post(`${id}/revert`, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not revert invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} id - ID of the invoice
   * @param {V2VerifyInvoiceRequest} v2Request - V2 verify invoice request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  async verify (id, v2Request) {
    try {
      const result = await (await getAxiosClient(this.baseURL)).post(`${id}/verify`, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not verify invoice in V2 API', error)
      throw error
    }
  }

  /**
   * @param {string} invoiceId - id of the invoice to be updated
   * @param {V2UpdateInvoiceRequest} v2Request - V2 invoice update request body
   * @returns {Promise<V2InvoiceResponse>} V2 response body
   */
  // eslint-disable-next-line no-unused-vars
  async update (invoiceId, v2Request) {
    try {
      const result =  await (await getAxiosClient(this.baseURL)).patch(invoiceId, v2Request)
      return result?.data
    }
    catch (error) {
      CError('Could not update invoice in V2 API', error)
      throw error
    }
  }
}

export default Invoice
